import React, { useContext, useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getProducts } from '../../clients/product_client';
import { listStorageNodeContents } from '../../clients/storage_client';
import S3MediaImage from '../Generic/S3MediaImage';
import ProductDetailModal from '../Product/ProductDetailModal';
import { defaultImageUrl } from '../../config_and_helpers/config';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { readSlotContentDisplay } from '../../utils/storagePlace';
import StorageLotMoveBar from './StorageLotMoveBar';
import StoragePlaceFigure from './StoragePlaceFigure';
import {
  getChildren,
  getStorageNodeTypeLabel,
  listStorageContentNodes,
  readSlotCount,
} from '../../utils/storageTree';

const LOG_CONTEXT = 'StorageNodeContents';

/**
 * Any opened storage node can hold a lot (room, unit, area, level, or box).
 *
 * @param {import('../../utils/storageTree').StorageNode|null|undefined} node
 * @returns {boolean}
 */
function canPlaceIntoNode(node) {
  return Boolean(node && node.id != null);
}

/**
 * Lab product whose CAT# matches a contents row.
 *
 * @param {string} token
 * @param {string} catNum
 * @returns {Promise<Object|null>}
 */
function lookupProductByCatNum(token, catNum) {
  return getProducts(token, () => {}, {
    searchInput: catNum,
    supplierCatalogueFilter: 'false',
  }).then(response => {
    const results = response?.success && Array.isArray(response.results) ? response.results : [];
    return (
      results.find(product => String(product?.cat_num || '').trim() === String(catNum).trim()) ||
      null
    );
  });
}

/**
 * What's inside a room, unit, shelf, drawer, rack, or plain box. Children are
 * listed even when the place is empty. Lots are the stock items in this place,
 * or in the boxes under it. A slotted box uses the slot chart instead. Place
 * item puts a lot on this node (any kind) — not only into a child box.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode|null} props.node
 * @param {Array} props.nodes
 * @param {Function} props.onClose
 * @param {Function} [props.onOpenNode] - (node) => void. Opens a child the same way as a map click
 * @param {Function} [props.onPlaceItem] - (node) => void. Opens inventory search for this place
 * @param {Function} [props.onLotsChanged] - Called after a lot is moved so card counts reload
 * @param {Function} [props.contentsLoader] - ({ reload }) => Promise. When set, the page owns the request so a hover can fill it first
 */
const StorageNodeContents = ({
  node,
  nodes = [],
  onClose,
  onOpenNode,
  onPlaceItem,
  onLotsChanged,
  contentsLoader = null,
}) => {
  const app = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const token = app?.token;
  const isSupplier = Boolean(app?.isSupplier);
  const children = node ? getChildren(nodes, node.id) : [];
  const boxes = node ? listStorageContentNodes(nodes, node.id) : [];
  const boxKey = boxes.map(box => String(box.id)).join(',');
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [productsByCat, setProductsByCat] = useState({});
  const [openProduct, setOpenProduct] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const nodeId = node?.id ?? null;

  useEffect(() => {
    setLots([]);
    setSelectedIds([]);
    setLoadError(null);
  }, [nodeId]);

  useEffect(() => {
    if (nodeId == null || !token) {
      setLots([]);
      setLoading(false);
      setLoadError(null);
      return undefined;
    }
    const targets = listStorageContentNodes(nodesRef.current, nodeId);
    let active = true;
    setLoading(true);
    setLoadError(null);
    if (contentsLoader) {
      logInfo(LOG_CONTEXT, 'load contents for place', {
        nodeId,
        boxCount: targets.length,
        shared: true,
      });
      contentsLoader({ reload: reloadKey > 0 }).then(result => {
        if (!active || !result || result.cancelled) return;
        if (result.error) {
          logError(LOG_CONTEXT, 'place contents failed', { nodeId });
          setLoadError('Could not load what is inside this place.');
          setLoading(false);
          return;
        }
        setLots(result.items || []);
        setLoading(false);
        logInfo(LOG_CONTEXT, 'place contents loaded', {
          nodeId,
          lotCount: (result.items || []).length,
        });
      });
      return () => {
        active = false;
      };
    }
    const controller = new AbortController();
    logInfo(LOG_CONTEXT, 'load contents for place', {
      nodeId,
      boxCount: targets.length,
    });
    Promise.all(
      targets.map(box =>
        listStorageNodeContents(token, box.id, { signal: controller.signal }).then(result => {
          if (result.cancelled) return { cancelled: true, items: [] };
          if (!result.success) return { failed: true, items: [] };
          return {
            failed: false,
            cancelled: false,
            items: (result.data || []).map(item => ({
              ...item,
              boxId: box.id,
              boxName: box.name,
            })),
          };
        })
      )
    )
      .then(groups => {
        if (!active) return;
        if (groups.some(group => group.cancelled)) return;
        if (groups.some(group => group.failed)) {
          logError(LOG_CONTEXT, 'place contents failed', { nodeId });
          setLoadError('Could not load what is inside this place.');
          setLoading(false);
          return;
        }
        const next = groups.flatMap(group => group.items);
        setLots(next);
        setLoading(false);
        logInfo(LOG_CONTEXT, 'place contents loaded', {
          nodeId,
          lotCount: next.length,
        });
      })
      .catch(error => {
        if (!active) return;
        logError(LOG_CONTEXT, 'place contents failed', error);
        setLoadError('Could not load what is inside this place.');
        setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
    // `nodes` is read from a ref. A new array with the same places must not
    // cancel the load that is already on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, token, boxKey, reloadKey, contentsLoader]);

  useEffect(() => {
    if (!token) {
      setProductsByCat({});
      return undefined;
    }
    const missing = [
      ...new Set(
        lots
          .map(item => readSlotContentDisplay(item))
          .filter(display => display.catNum && (display.productId == null || !display.imageUrl))
          .map(display => display.catNum)
      ),
    ];
    if (!missing.length) {
      setProductsByCat({});
      return undefined;
    }
    let active = true;
    Promise.all(
      missing.map(catNum =>
        lookupProductByCatNum(token, catNum)
          .then(product => [catNum, product])
          .catch(error => {
            logError(LOG_CONTEXT, 'contents product lookup failed', { catNum, error });
            return [catNum, null];
          })
      )
    ).then(pairs => {
      if (!active) return;
      setProductsByCat(Object.fromEntries(pairs));
    });
    return () => {
      active = false;
    };
  }, [lots, token]);

  const empty = !loading && !loadError && children.length === 0 && lots.length === 0;
  const canPlaceHere = canPlaceIntoNode(node) && typeof onPlaceItem === 'function';

  return (
    <>
      <Modal
        show={Boolean(node)}
        onHide={onClose}
        size="xl"
        centered
        enforceFocus={false}
        className="storage-node-contents-modal"
        aria-labelledby="storage-node-contents-title"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h2" id="storage-node-contents-title">
            {node?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {node ? (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <p className="text-muted small mb-0">{getStorageNodeTypeLabel(node)}</p>
              {canPlaceHere ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    logInfo(LOG_CONTEXT, 'place item opened from place contents', {
                      nodeId: node.id,
                      kind: node.kind ?? null,
                      subtype: node.subtype ?? null,
                    });
                    onPlaceItem(node);
                  }}
                >
                  Place item
                </Button>
              ) : null}
            </div>
          ) : null}
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" role="status" />
            </div>
          ) : null}
          {children.length ? (
            <div
              className={[
                'storage-place-scene',
                `storage-place-scene--${node?.subtype || node?.kind || 'place'}`,
              ].join(' ')}
              role="group"
              aria-label={node?.name || 'Place'}
            >
              <ul className="storage-place-scene__items">
                {children.map(child => {
                  const slots = readSlotCount(child);
                  const key = child.subtype || child.kind || 'box';
                  return (
                    <li key={child.id}>
                      <button
                        type="button"
                        className={`storage-place-card storage-place-card--${key}`}
                        onClick={() => onOpenNode?.(child)}
                      >
                        <StoragePlaceFigure node={child} />
                        <span className="storage-place-card__name">{child.name}</span>
                        <span className="storage-place-card__type">
                          {slots
                            ? `${slots} ${slots === 1 ? 'slot' : 'slots'}`
                            : getStorageNodeTypeLabel(child)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {node?.subtype === 'shelf' ? (
                <div className="storage-place-scene__plank" aria-hidden="true" />
              ) : null}
            </div>
          ) : null}
          {lots.length ? (
            <>
            {!isViewer ? (
            <StorageLotMoveBar
              token={token}
              nodes={nodes}
              lots={lots.filter(item => selectedIds.includes(String(item.id)))}
              onMoved={({ failed, moved, storageNodeId }) => {
                setSelectedIds((failed || []).map(lot => String(lot.id)));
                setReloadKey(key => key + 1);
                onLotsChanged?.({ moved, storageNodeId });
              }}
            />
            ) : null}
            <ul className="storage-node-contents__lots">
              {lots.map(item => {
                const preview = readSlotContentDisplay(item);
                const display = readSlotContentDisplay(item, productsByCat[preview.catNum]);
                const lotLine = item?.batch ? `Lot ${display.lot}` : display.lot;
                const showCat = display.catNum && display.catNum !== display.name;
                const where =
                  item.slot_index != null
                    ? `${item.boxName} · slot ${item.slot_index}`
                    : item.boxName;
                const open = () => {
                  if (display.productId == null) return;
                  logInfo(LOG_CONTEXT, 'open inventory from place contents', {
                    productId: display.productId,
                    nodeId: node?.id,
                  });
                  setOpenProduct({
                    id: display.productId,
                    cat_num: display.catNum,
                    name: display.name,
                    first_image: display.imageUrl || undefined,
                  });
                };
                const selected = selectedIds.includes(String(item.id));
                return (
                  <li key={`${item.boxId}-${item.id}`} className="storage-node-contents__lot-wrap">
                    {!isViewer ? (
                    <input
                      type="checkbox"
                      className="storage-lot-select"
                      checked={selected}
                      aria-label={`Select ${display.catNum || display.name}, ${lotLine}`}
                      onClick={event => event.stopPropagation()}
                      onChange={() => {
                        setSelectedIds(current =>
                          selected
                            ? current.filter(id => id !== String(item.id))
                            : [...current, String(item.id)]
                        );
                      }}
                    />
                    ) : null}
                    <div className="storage-node-contents__lot">
                      <S3MediaImage
                        url={display.imageUrl || defaultImageUrl}
                        alt=""
                        className="storage-node-contents__photo"
                        fallbackUrl={defaultImageUrl}
                      />
                      <span className="storage-node-contents__lot-text">
                        {display.productId != null ? (
                          <button
                            type="button"
                            className="storage-node-contents__open"
                            onClick={open}
                            aria-label={`Open ${display.name}`}
                          >
                            {display.name}
                          </button>
                        ) : (
                          <span className="storage-node-contents__open">{display.name}</span>
                        )}
                        {showCat ? (
                          <span className="storage-node-contents__cat">{display.catNum}</span>
                        ) : null}
                        <span className="storage-node-contents__lot-line">{lotLine}</span>
                        {node?.kind !== 'box' ? (
                          <span className="storage-node-contents__meta">{where}</span>
                        ) : null}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
            </>
          ) : null}
          {loadError ? <p className="mb-0">{loadError}</p> : null}
          {empty ? <p className="mb-0">Nothing inside.</p> : null}
        </Modal.Body>
      </Modal>
      {openProduct ? (
        <ProductDetailModal
          isSupplier={isSupplier}
          productObj={openProduct}
          productId={openProduct.id}
          hideTrigger
          show
          onHide={() => setOpenProduct(null)}
        />
      ) : null}
    </>
  );
};

export default StorageNodeContents;
